import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wrench, LogOut, Search, UserPlus, Play, Pause, CheckCircle2, Sparkles, Flag, Truck } from "lucide-react";
import { api, peso, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Btn, Field, LiveTimer, StatusBadge, cx, useInterval } from "../../components/kit";

const VEHICLE_TYPES = ["Sedan", "Hatchback", "SUV", "Pickup", "Van", "Motorcycle", "Other"];
const PAY_METHODS = ["Cash", "GCash", "Maya", "Debit Card", "Credit Card", "Bank Transfer", "Corporate"];

export default function CrewApp() {
  const { user, logout } = useAuth();
  if (!user) return <CrewLogin />;
  return <CrewDashboard user={user} logout={logout} />;
}

function CrewLogin() {
  const { loginCrew } = useAuth();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try { await loginCrew(username, pin); toast.success("Logged in"); }
    catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-16 pb-28">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-cyan-500 flex items-center justify-center"><Wrench className="text-slate-950" size={22} /></div>
        <h1 className="font-head text-lg font-extrabold">Cool Car Crew</h1>
      </div>
      <Card className="animate-in">
        <h2 className="font-head text-xl font-bold mb-1">Crew Login</h2>
        <p className="text-sm text-slate-400 mb-5">Sign in with your username and PIN.</p>
        <div className="space-y-3">
          <Field label="Username" testid="crew-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="cs01" />
          <Field label="PIN" type="password" testid="crew-pin" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" />
          <Btn className="w-full" data-testid="crew-login-btn" onClick={submit} disabled={loading}>{loading ? "Signing in..." : "Sign In"}</Btn>
        </div>
        <div className="mt-4 text-xs text-slate-500 leading-relaxed">
          Demo: cs01/1111 · cash01/2222 · bay01/3333 · sup01/4444
        </div>
      </Card>
    </div>
  );
}

function CrewDashboard({ user, logout }) {
  const [tab, setTab] = useState("ops");
  const tabs = [["ops", "Ops"], ["new", "New Order"], ["pay", "Payments"], ["cash", "Cash"]];

  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-6 pb-28">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-head text-lg font-extrabold leading-none">Cool Car Crew</h1>
          <div className="text-xs text-cyan-400/90">{user.name} · {user.role}</div>
        </div>
        <button data-testid="crew-logout" onClick={logout} className="text-slate-400 flex items-center gap-1 text-sm"><LogOut size={16} /> Exit</button>
      </div>
      <div className="flex gap-1 bg-[#1C2541] p-1 rounded-xl mb-4">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`crew-tab-${k}`} onClick={() => setTab(k)}
            className={cx("flex-1 py-2 rounded-lg text-sm font-semibold", tab === k ? "bg-cyan-500 text-slate-950" : "text-slate-400")}>{l}</button>
        ))}
      </div>
      {tab === "ops" && <OpsTab />}
      {tab === "new" && <NewOrderTab />}
      {tab === "pay" && <PaymentsTab />}
      {tab === "cash" && <CashTab />}
    </div>
  );
}

function OpsTab() {
  const [queue, setQueue] = useState([]);
  const [bays, setBays] = useState([]);
  const load = async () => {
    try {
      const [q, b] = await Promise.all([api.get("/queue"), api.get("/bays")]);
      setQueue(q.data); setBays(b.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useInterval(load, 4000);

  const assign = async (orderId, bayId) => {
    try { await api.post(`/orders/${orderId}/assign-bay`, { bay_id: bayId }); toast.success("Assigned to bay"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doAction = async (orderId, action) => {
    try { await api.post(`/orders/${orderId}/workflow`, { action }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const availableBays = bays.filter((b) => b.status === "available");

  return (
    <div className="space-y-4 animate-in">
      <div>
        <div className="ccc-label mb-2">Waiting Queue ({queue.length})</div>
        {queue.length === 0 && <Card><div className="text-sm text-slate-500 text-center py-2">No vehicles waiting</div></Card>}
        <div className="space-y-2">
          {queue.map((o) => (
            <Card key={o.id} data-testid={`queue-item-${o.id}`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-bold">{o.vehicle.plate}</div>
                  <div className="text-xs text-slate-400">{o.vehicle.type} · {o.service.name}</div>
                </div>
                <StatusBadge status={o.status} />
              </div>
              {availableBays.length > 0 ? (
                <div className="flex gap-1.5 mt-3 flex-wrap">
                  {availableBays.map((b) => (
                    <button key={b.id} data-testid={`assign-${o.id}-${b.number}`} onClick={() => assign(o.id, b.id)}
                      className="px-3 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/30 text-xs font-bold">→ Bay {b.number}</button>
                  ))}
                </div>
              ) : <div className="text-xs text-amber-400 mt-2">No available bays</div>}
            </Card>
          ))}
        </div>
      </div>

      <div>
        <div className="ccc-label mb-2">Bays</div>
        <div className="space-y-2">
          {bays.map((b) => (
            <Card key={b.id} data-testid={`bay-card-${b.number}`}>
              <div className="flex justify-between items-center">
                <div className="font-head font-bold">Bay {b.number}</div>
                <span className={cx("text-xs font-bold px-2 py-0.5 rounded-full",
                  b.status === "available" ? "text-emerald-400 bg-emerald-500/10" : b.status === "occupied" ? "text-cyan-400 bg-cyan-500/10" : "text-amber-400 bg-amber-500/10")}>
                  {b.status.toUpperCase()}
                </span>
              </div>
              {b.order ? (
                <div className="mt-2">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{b.order.vehicle.plate} · {b.order.service.name}</div>
                    <StatusBadge status={b.order.status} />
                  </div>
                  {b.order.timers?.service_start && (
                    <div className="mt-2"><LiveTimer from={b.order.timers.service_start} to={b.order.timers.service_finish} targetMin={b.order.service.duration_min} testid={`bay-timer-${b.number}`} /></div>
                  )}
                  <BayControls order={b.order} doAction={doAction} />
                </div>
              ) : <div className="text-xs text-slate-500 mt-2">Idle</div>}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function BayControls({ order, doAction }) {
  const s = order.status;
  const B = ({ action, label, icon: Icon, variant = "primary" }) => (
    <Btn variant={variant} data-testid={`wf-${order.id}-${action}`} className="text-xs px-3 py-2 flex items-center gap-1" onClick={() => doAction(order.id, action)}>
      <Icon size={14} /> {label}
    </Btn>
  );
  return (
    <div className="flex gap-1.5 mt-3 flex-wrap">
      {s === "bay_assigned" && <B action="start" label="Start" icon={Play} />}
      {s === "washing" && !order.paused && <B action="pause" label="Pause" icon={Pause} variant="warn" />}
      {s === "washing" && order.paused && <B action="resume" label="Resume" icon={Play} />}
      {s === "washing" && <B action="complete_wash" label="Complete Wash" icon={CheckCircle2} />}
      {s === "drying" && <B action="quality_check" label="Quality Check" icon={Sparkles} variant="ghost" />}
      {s === "quality_check" && <B action="ready" label="Mark Ready" icon={Flag} variant="success" />}
      {s === "ready" && <B action="release" label="Release" icon={Truck} variant="success" />}
    </div>
  );
}

function NewOrderTab() {
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [svc, setSvc] = useState(null);
  const [picked, setPicked] = useState({});
  const [quote, setQuote] = useState(null);
  const [walkin, setWalkin] = useState(false);

  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); api.get("/addons").then((r) => setAddons(r.data)); }, []);
  const addonIds = useMemo(() => Object.keys(picked).filter((k) => picked[k]), [picked]);
  useEffect(() => {
    if (!svc || !vehicle) { setQuote(null); return; }
    api.post("/pricing/quote", { service_id: svc.id, vehicle_type: vehicle.vehicle_type, addon_ids: addonIds }).then((r) => setQuote(r.data)).catch(() => {});
  }, [svc, vehicle, addonIds]);

  const lookup = async () => {
    try {
      const { data } = await api.post("/customers/lookup", { plate: q, mobile: q, name: q });
      if (data.found) { setCustomer(data.customer); setVehicle(data.matched_vehicle || data.customer.vehicles?.[0]); toast.success("Found " + data.customer.name); }
      else toast.info("Not found — use walk-in registration");
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/orders", { customer_id: customer.id, vehicle_id: vehicle.id, service_id: svc.id, addon_ids: addonIds, source: "crew" });
      toast.success(`Order ${data.transaction_no} created → queue`);
      setSvc(null); setPicked({}); setQuote(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 animate-in">
      <Card>
        <div className="ccc-label mb-2">Customer Lookup</div>
        <div className="flex gap-2">
          <input className="ccc-input" data-testid="crew-lookup-input" placeholder="Plate / mobile / name" value={q} onChange={(e) => setQ(e.target.value)} />
          <Btn data-testid="crew-lookup-btn" onClick={lookup}><Search size={18} /></Btn>
        </div>
        <button className="text-cyan-400 text-sm font-semibold mt-3 flex items-center gap-1" data-testid="crew-walkin-toggle" onClick={() => setWalkin(!walkin)}>
          <UserPlus size={16} /> {walkin ? "Hide" : "New"} walk-in customer
        </button>
        {walkin && <WalkIn onDone={(c) => { setCustomer(c); setVehicle(c.vehicles?.[0]); setWalkin(false); }} />}
      </Card>

      {customer && (
        <>
          <Card>
            <div className="font-bold">{customer.name} <span className="text-xs text-cyan-400">({customer.status})</span></div>
            <div className="text-xs text-slate-400">{customer.mobile}</div>
            <select className="ccc-input mt-2" data-testid="crew-vehicle-select" value={vehicle?.id || ""} onChange={(e) => setVehicle((customer.vehicles || []).find((v) => v.id === e.target.value))}>
              {(customer.vehicles || []).map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.vehicle_type}</option>)}
            </select>
          </Card>
          <div>
            <div className="ccc-label mb-2">Service</div>
            <div className="space-y-2">
              {services.map((s) => (
                <button key={s.id} data-testid={`crew-service-${s.id}`} onClick={() => setSvc(s)} className={cx("w-full text-left rounded-xl p-3 border flex justify-between", svc?.id === s.id ? "bg-cyan-500/10 border-cyan-400/60" : "bg-[#1C2541] border-slate-700/60")}>
                  <span className="text-sm">{s.name}</span>
                  <span className="mono text-cyan-400 text-sm">{vehicle ? peso(s.prices?.[vehicle.vehicle_type] ?? s.base_price) : ""}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="ccc-label mb-2">Add-ons</div>
            <div className="grid grid-cols-2 gap-2">
              {addons.map((a) => (
                <button key={a.id} data-testid={`crew-addon-${a.id}`} onClick={() => setPicked({ ...picked, [a.id]: !picked[a.id] })} className={cx("rounded-xl p-2.5 border text-left", picked[a.id] ? "bg-cyan-500/10 border-cyan-400/60" : "bg-[#1C2541] border-slate-700/60")}>
                  <div className="text-sm">{a.name}</div><div className="mono text-xs text-cyan-400">{peso(a.price)}</div>
                </button>
              ))}
            </div>
          </div>
          {quote && <Card className="border-cyan-500/30"><div className="flex justify-between items-center"><span className="font-bold">Total</span><span className="mono text-2xl font-extrabold text-cyan-400" data-testid="crew-total">{peso(quote.total)}</span></div></Card>}
          <Btn className="w-full" data-testid="crew-create-order" onClick={create} disabled={!svc}>Create Work Order</Btn>
        </>
      )}
    </div>
  );
}

function WalkIn({ onDone }) {
  const [f, setF] = useState({ name: "", mobile: "", plate: "", vehicle_type: "Sedan", make: "", model: "", color: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name || !f.mobile || !f.plate) return toast.error("Name, mobile, plate required");
    try {
      const { data } = await api.post("/customers", { name: f.name, mobile: f.mobile, vehicle: { plate: f.plate, vehicle_type: f.vehicle_type, make: f.make, model: f.model, color: f.color } });
      toast.success("Walk-in registered"); onDone(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-2 mt-3 pt-3 border-t border-slate-700/50">
      <Field label="Name" testid="walkin-name" value={f.name} onChange={set("name")} />
      <Field label="Mobile" testid="walkin-mobile" value={f.mobile} onChange={set("mobile")} />
      <Field label="Plate" testid="walkin-plate" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} />
      <select className="ccc-input" data-testid="walkin-vtype" value={f.vehicle_type} onChange={set("vehicle_type")}>
        {VEHICLE_TYPES.map((v) => <option key={v}>{v}</option>)}
      </select>
      <Btn className="w-full" data-testid="walkin-save" onClick={save}>Register Walk-in</Btn>
    </div>
  );
}

function PaymentsTab() {
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Cash");
  const [reference, setReference] = useState("");
  const load = () => api.get("/orders", { params: { active: true } }).then((r) => setOrders(r.data.filter((o) => o.payment_status !== "paid")));
  useEffect(() => { load(); }, []);
  useInterval(load, 5000);

  const pick = (o) => { setSel(o); setAmount(String(Math.max(0, o.total - (o.amount_paid || 0)))); };
  const record = async () => {
    if (method !== "Cash" && !reference) return toast.error("Reference number required");
    try {
      const { data } = await api.post(`/orders/${sel.id}/payments`, { amount: Number(amount), method, reference });
      toast.success(`Payment recorded — ${data.payment_status.toUpperCase()}`);
      setSel(null); setReference(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3 animate-in">
      <div className="ccc-label">Unpaid Orders</div>
      {orders.length === 0 && <Card><div className="text-center text-slate-500 py-2 text-sm">Nothing to collect</div></Card>}
      {orders.map((o) => (
        <Card key={o.id} data-testid={`pay-order-${o.id}`} className={cx(sel?.id === o.id && "border-cyan-400/60")} onClick={() => pick(o)}>
          <div className="flex justify-between">
            <div><div className="font-bold">{o.transaction_no}</div><div className="text-xs text-slate-400">{o.vehicle.plate} · {o.service.name}</div></div>
            <div className="mono font-bold text-cyan-400">{peso(o.total)}</div>
          </div>
        </Card>
      ))}
      {sel && (
        <Card className="border-cyan-500/30 space-y-3">
          <div className="font-bold">Record Payment · {sel.transaction_no}</div>
          <Field label="Amount" type="number" testid="crew-pay-amount" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <div className="grid grid-cols-3 gap-1.5">
            {PAY_METHODS.map((m) => <button key={m} data-testid={`crew-pay-method-${m}`} onClick={() => setMethod(m)} className={cx("py-2 rounded-lg text-xs font-semibold border", method === m ? "bg-cyan-500 text-slate-950 border-cyan-400" : "bg-[#0B132B] border-slate-700 text-slate-300")}>{m}</button>)}
          </div>
          {method !== "Cash" && <Field label="Reference No." testid="crew-pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} />}
          <Btn className="w-full" data-testid="crew-pay-submit" onClick={record}>Confirm Payment</Btn>
        </Card>
      )}
    </div>
  );
}

function CashTab() {
  const [session, setSession] = useState(null);
  const [opening, setOpening] = useState("");
  const load = () => api.get("/cash/current").then((r) => setSession(r.data));
  useEffect(() => { load(); }, []);

  const open = async () => {
    try { await api.post("/cash/open", { opening_amount: Number(opening) || 0 }); toast.success("Cash session opened"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3 animate-in">
      {session?.open ? (
        <Card className="border-cyan-500/30">
          <div className="ccc-label mb-1">Open Cash Session</div>
          <div className="text-sm">Opened by {session.opened_by}</div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><div className="text-xs text-slate-400">Opening</div><div className="mono font-bold">{peso(session.opening_amount)}</div></div>
            <div><div className="text-xs text-slate-400">Cash Payments</div><div className="mono font-bold">{peso(session.cash_payments)}</div></div>
            <div className="col-span-2"><div className="text-xs text-slate-400">Expected Cash</div><div className="mono text-xl font-extrabold text-cyan-400" data-testid="crew-expected-cash">{peso(session.expected_cash)}</div></div>
          </div>
          <p className="text-xs text-slate-500 mt-3">Closing count & reconciliation is done in the Admin → Cash tab.</p>
        </Card>
      ) : (
        <Card>
          <div className="ccc-label mb-2">Open Cash Drawer</div>
          <Field label="Opening Cash Balance" type="number" testid="crew-cash-opening" value={opening} onChange={(e) => setOpening(e.target.value)} />
          <Btn className="w-full mt-3" data-testid="crew-cash-open" onClick={open}>Open Session</Btn>
        </Card>
      )}
    </div>
  );
}
